import React, { useCallback, useEffect, useRef, useState } from "react";

// Type a city, pick one, and the coordinates AND THE TIME ZONE come with it.
//
// The zone is the point. Coordinates a tenth of a degree out move the Midheaven
// a tenth of a degree; the wrong zone moves the Ascendant by fifteen. Picking a
// city is the only way a person can reliably supply an IANA identifier.
//
// The gazetteer is local, so nobody is asked where this person was born.
export default function PlaceSearch({ label, onChoose, onClear }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [searching, setSearching] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      setSearching(true);
      fetch(`/astrology/places?q=${encodeURIComponent(term)}`)
        .then((response) => response.json())
        .then((body) => {
          setResults(body.results || []);
          setActive(-1);
          setOpen(true);
        })
        .finally(() => setSearching(false));
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  // Clicking away closes the list without choosing.
  useEffect(() => {
    const onDocumentClick = (event) => {
      if (box.current && !box.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  const choose = useCallback(
    (place) => {
      onChoose(place);
      setQuery("");
      setResults([]);
      setOpen(false);
    },
    [onChoose]
  );

  const onKeyDown = (event) => {
    if (!open || results.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (event.key === "Enter" && active >= 0) {
      event.preventDefault();
      choose(results[active]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="placesearch" ref={box}>
      <label className="chartform__field">
        <span>Place of birth</span>
        <input
          type="search"
          value={query}
          placeholder={label || "Search for a city"}
          autoComplete="off"
          role="combobox"
          aria-expanded={open && results.length > 0}
          aria-controls="place-results"
          aria-autocomplete="list"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => results.length > 0 && setOpen(true)}
          data-testid="field-place"
        />
        <small>
          {label ? (
            <>
              Using <strong data-testid="chosen-place">{label}</strong>.{" "}
              <button type="button" className="placesearch__clear" onClick={onClear}
                      data-testid="clear-place">
                Enter coordinates instead
              </button>
            </>
          ) : (
            <>A city carries its time zone, which matters more than its coordinates do.</>
          )}
        </small>
      </label>

      {open && results.length > 0 && (
        <ul className="placesearch__results" id="place-results" role="listbox"
            data-testid="place-results">
          {results.map((place, index) => (
            <li key={place.id} role="option" aria-selected={index === active}>
              <button
                type="button"
                className={index === active ? "placesearch__hit placesearch__hit--on" : "placesearch__hit"}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(place)}
                data-testid={`place-${place.id}`}
              >
                <span className="placesearch__label">{place.label}</span>
                <span className="placesearch__zone">{place.timezone}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Two Bostons and three Worcesters: never silently take the biggest. */}
      {open && results.length === 0 && query.trim().length >= 2 && !searching && (
        <p className="placesearch__none" data-testid="place-none">
          No city found. Try a larger place nearby, or enter coordinates directly —
          the gazetteer holds places over 15,000 people.
        </p>
      )}
    </div>
  );
}
